import {
  SALES,
  SALES_DIRECTOR_GM,
  SALES_MANAGER,
} from 'library/api-common/src/lib/utils/appliationConstants';

export const PROJECT_MANAGEMENT_ROLE_OPTIONS = [
  { id: SALES, name: 'Sales' },
  { id: SALES_MANAGER, name: 'Sales Manager' },
  { id: SALES_DIRECTOR_GM, name: 'Sales Director/GM' },
] as const;

export const PROJECT_MANAGEMENT_ROLE_IDS = [
  SALES,
  SALES_MANAGER,
  SALES_DIRECTOR_GM,
] as const;

export type ProjectManagementRoleId =
  (typeof PROJECT_MANAGEMENT_ROLE_IDS)[number];

/** Default PM role for legacy toggle-based users without a stored role. */
export const DEFAULT_PROJECT_MANAGEMENT_ROLE_ID = SALES_DIRECTOR_GM;

export function isProjectManagementRoleId(
  roleId: number | null | undefined,
): roleId is ProjectManagementRoleId {
  return (
    roleId != null &&
    (PROJECT_MANAGEMENT_ROLE_IDS as readonly number[]).includes(roleId)
  );
}

/**
 * Resolves the effective PM role for layout and permission checks.
 * Native PM users (projectId=2) use their assigned role; toggle-based users use stored PM role.
 */
export function resolveProjectManagementRoleId(
  roles: Array<{ id?: number; projectId?: number }> | undefined,
  projectManagementRoleId?: number | null,
): ProjectManagementRoleId {
  const roleList = roles ?? [];
  const nativePmRole = roleList.find(
    (role) =>
      Number(role.projectId) === 2 &&
      isProjectManagementRoleId(Number(role.id)),
  );
  if (nativePmRole?.id != null && isProjectManagementRoleId(nativePmRole.id)) {
    return nativePmRole.id;
  }

  if (isProjectManagementRoleId(projectManagementRoleId)) {
    return projectManagementRoleId;
  }

  return DEFAULT_PROJECT_MANAGEMENT_ROLE_ID;
}

/**
 * Reads the active PM role id from browser storage when the user is in PM mode.
 */
export function readStoredProjectManagementRoleId(): number | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const stored = localStorage.getItem('projectManagementRoleId');
  const parsed = stored != null ? Number(stored) : NaN;
  return isProjectManagementRoleId(parsed) ? parsed : null;
}

export function getActiveProjectManagementRoleId(): number | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  if (localStorage.getItem('isProjectManager') !== 'true') {
    return null;
  }
  return readStoredProjectManagementRoleId();
}

/**
 * Effective role id for PM screens: stored PM role on PM routes/toggle mode, otherwise Store360 role.
 */
export function resolveEffectivePmScreenRoleId(
  store360RoleId: number | null | undefined,
): number {
  const storedPmRole = readStoredProjectManagementRoleId();
  const hasPmAccess =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('IsProjectManagement') === 'true';
  const onPmRoute =
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/project-management/');
  const inPmToggleMode =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('isProjectManager') === 'true';

  if (hasPmAccess && isProjectManagementRoleId(storedPmRole)) {
    if (inPmToggleMode || onPmRoute) {
      return storedPmRole;
    }
  }

  return store360RoleId ?? 0;
}
